import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

const DISPOSITIONS = ['restock', 'quarantine', 'damaged', 'write_off', 'return_to_supplier', 'repair'];

export default function InventoryReturns() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/returns?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const [wRes, pRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
    ]);
    setWarehouses(await wRes.json());
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openView = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/returns/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>↩️ Customer Returns</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Return</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending_inspection">Pending Inspection</option>
          <option value="inspected">Inspected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Return #', 'Warehouse', 'Client', 'Order #', 'Lines', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No returns</td></tr>}
              {list.map(r => (
                <tr key={r.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#v" onClick={e => { e.preventDefault(); openView(r.id); }} style={{ color: '#3b82f6' }}>{r.return_number}</a>
                  </td>
                  <td style={tdStyle}>{r.warehouse?.name}</td>
                  <td style={tdStyle}>{r.client_name || '—'}</td>
                  <td style={tdStyle}>{r.order_number || '—'}</td>
                  <td style={tdStyle}>{r.lines_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={r.status} /></td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openView(r.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreateReturnModal companyId={companyId} warehouses={warehouses} products={products}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
      {viewing && (
        <ReturnDetail companyId={companyId} ret={viewing}
          onClose={() => { setViewing(null); fetchList(); }} onRefresh={() => openView(viewing.id)} />
      )}
    </div>
  );
}

function CreateReturnModal({ companyId, warehouses, products, onClose, onSaved }) {
  const [warehouseId, setWarehouseId] = useState('');
  const [clientName, setClientName] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([{ product_id: '', quantity: '' }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { product_id: '', quantity: '' }]);
  const updateLine = (i, field, val) => setLines(lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!warehouseId) { alert('Warehouse is required'); return; }
    const validLines = lines.filter(l => l.product_id && l.quantity);
    if (validLines.length === 0) { alert('At least one line is required'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/returns`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ warehouse_id: warehouseId, client_name: clientName || null, order_number: orderNumber || null, return_reason: returnReason || null, notes, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Customer Return</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Warehouse *">
            <select style={inputStyle} value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
              <option value="">— Select —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Order # (reference)"><input style={inputStyle} value={orderNumber} onChange={e => setOrderNumber(e.target.value)} /></FormRow></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><FormRow label="Client Name"><input style={inputStyle} value={clientName} onChange={e => setClientName(e.target.value)} /></FormRow></div>
          <div style={{ flex: 1 }}><FormRow label="Return Reason"><input style={inputStyle} value={returnReason} onChange={e => setReturnReason(e.target.value)} /></FormRow></div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>LINES</div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select style={{ ...inputStyle, flex: 2 }} value={l.product_id} onChange={e => updateLine(i, 'product_id', e.target.value)}>
              <option value="">— Product —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty" style={{ ...inputStyle, flex: 1 }} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
            <button style={btnSmall} onClick={() => removeLine(i)}>✕</button>
          </div>
        ))}
        <button style={btnSecondary} onClick={addLine}>+ Add Line</button>

        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50, marginTop: 10 }} value={notes} onChange={e => setNotes(e.target.value)} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function ReturnDetail({ companyId, ret, onClose, onRefresh }) {
  const [dispositions, setDispositions] = useState(() => {
    const d = {};
    (ret.lines || []).forEach(l => { d[l.id] = { disposition: l.disposition !== 'pending' ? l.disposition : 'restock' }; });
    return d;
  });
  const [busy, setBusy] = useState(false);

  const setDisp = (lineId, val) => setDispositions(d => ({ ...d, [lineId]: { disposition: val } }));

  const inspect = async () => {
    setBusy(true);
    const lines = (ret.lines || []).map(l => ({ line_id: l.id, disposition: dispositions[l.id]?.disposition || 'restock' }));
    const res = await fetch(`${API}/companies/${companyId}/inventory/returns/${ret.id}/inspect`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ lines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed'); }
    setBusy(false); onRefresh();
  };

  const process = async () => {
    if (!window.confirm('Process this return? This will move stock according to each line\'s disposition.')) return;
    setBusy(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/returns/${ret.id}/process`, { method: 'POST', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed'); }
    setBusy(false); onRefresh();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{ret.return_number} <Badge value={ret.status} /></h3>
        <div style={{ marginBottom: 10 }}><b>Warehouse:</b> {ret.warehouse?.name} &nbsp; <b>Client:</b> {ret.client_name || '—'}</div>
        <table style={tableStyle}>
          <thead><tr>{['Product', 'Qty', 'Disposition'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(ret.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.product?.name || l.product_id}</td>
                <td style={tdStyle}>{fmtQty(l.quantity)}</td>
                <td style={tdStyle}>
                  {ret.status === 'pending_inspection' ? (
                    <select style={{ ...inputStyle, width: 160 }} value={dispositions[l.id]?.disposition || 'restock'} onChange={e => setDisp(l.id, e.target.value)}>
                      {DISPOSITIONS.map(d => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
                    </select>
                  ) : (l.disposition || '—').replace(/_/g, ' ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {ret.status === 'pending_inspection' && <button style={btnPrimary} onClick={inspect} disabled={busy}>Save Inspection</button>}
          {ret.status === 'inspected' && <button style={btnPrimary} onClick={process} disabled={busy}>Process Return</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
